import { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import {
  Landmark,
  Loader2,
  AlertCircle,
  Euro,
  Percent,
  PiggyBank,
  Activity,
  Sparkles,
  Building2,
  User,
  Bed,
  Bath,
  Square,
  Wallet,
  Briefcase,
  Home,
  MapPin,
  Gauge,
  X,
  ChevronRight,
  FileText,
  MessageSquare,
  Plus,
  Bot,
  Heart,
  ExternalLink,
  Pencil,
  Trash2,
  Check,
} from 'lucide-react';
import Popup from '../components/Popup';
import { useAuth } from '../contexts/AuthContext';
import {
  getMortgageRecs,
  getClientInfo,
  getUserSavedChats,
  loadChat,
  getClientsAffordability,
  getUserFavorites,
  createPropertyReportRecord,
  movePropertyReportToValidated,
  updatePropertyReportRecordLocation,
  deletePropertyReportAsset,
  deletePropertyReportRecord,
  uploadPropertyReportPdf,
  updateClientInfo,
  renameChat,
  deleteChat,
} from '../supabase';

function formatEuro(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : parseFloat(String(value).replace(',', '.'));
  if (Number.isNaN(n)) return null;
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(n);
}

function formatLtv(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : parseFloat(String(value).replace(',', '.'));
  if (Number.isNaN(n)) return null;
  const pct = n <= 1 ? Math.round(n * 1000) / 10 : n;
  return `${pct}%`;
}

function formatAprDisplay(value) {
  if (value === null || value === undefined) return null;
  const s = String(value).trim();
  if (!s) return null;
  if (s.includes('%')) return s;
  const n = parseFloat(s.replace(',', '.'));
  if (!Number.isNaN(n)) return `${n}%`;
  return s;
}

function displayOrDash(value, formatter) {
  if (value === null || value === undefined || value === '') return '—';
  if (formatter) return formatter(value) ?? '—';
  return String(value);
}

const minPriceOptions = [0, 50000, 75000, 100000, 125000, 150000, 175000, 200000, 225000, 250000, 275000, 300000, 325000, 350000, 375000, 400000, 425000, 450000, 475000, 500000, 550000, 600000, 650000, 700000, 750000, 800000, 850000, 900000, 950000, 1000000, 1100000, 1200000, 1300000, 1400000, 1500000, 1600000, 1700000, 1800000, 1900000, 2000000, 2250000, 2500000, 2750000, 3000000, 3500000, 4000000];
const maxPriceOptions = [75000, 100000, 125000, 150000, 175000, 200000, 225000, 250000, 275000, 300000, 325000, 350000, 375000, 400000, 425000, 450000, 475000, 500000, 550000, 600000, 650000, 700000, 750000, 800000, 850000, 900000, 950000, 1000000, 1100000, 1200000, 1300000, 1400000, 1500000, 1600000, 1700000, 1800000, 1900000, 2000000, 2250000, 2500000, 2750000, 3000000, 3500000, 4000000];
const minSizeOptions = [0, 60, 80, 100, 120, 140, 160, 180, 200, 220, 240, 260, 280, 300, 320, 340, 360, 380, 400, 420, 440, 460, 480, 500];
const bathroomsOptions = [1, 2, 3];
const bedroomsOptions = [1, 2, 3, 4];
const DOCUMENTS_REQUIRED_TOTAL = 10; // Ajustaremos este objetivo cuando nos indiques el número final.

const REQUIRED_UPLOAD_DOCUMENTS = [
  { key: 'dni_nie', label: 'DNI o NIE en vigor.', aliases: ['dni', 'nie'] },
  {
    key: 'estado_civil',
    label: 'Estado civil (certificado de matrimonio, divorcio, etc.).',
    aliases: ['estado civil', 'matrimonio', 'divorcio'],
  },
  { key: 'nominas_3', label: 'Ultimas 3 nominas.', aliases: ['nomina', 'nominas'] },
  {
    key: 'renta',
    label: 'Declaracion de la renta mas reciente.',
    aliases: ['renta', 'declaracion'],
  },
  { key: 'vida_laboral', label: 'Vida laboral actualizada.', aliases: ['vida laboral'] },
  { key: 'contrato_trabajo', label: 'Contrato de trabajo.', aliases: ['contrato trabajo', 'contrato'] },
  {
    key: 'extractos_bancarios',
    label: 'Extractos bancarios de los ultimos 3-6 meses.',
    aliases: ['extracto', 'bancario', 'bank statement'],
  },
  {
    key: 'deudas_prestamos',
    label: 'Informacion sobre otros prestamos o deudas.',
    aliases: ['deuda', 'prestamo', 'prestamos'],
  },
];

function normalizeDocText(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function detectRequiredDocumentKeyFromText(text) {
  const normalized = normalizeDocText(text);
  if (!normalized) return null;
  const match = REQUIRED_UPLOAD_DOCUMENTS.find((doc) =>
    doc.aliases.some((alias) => normalized.includes(normalizeDocText(alias)))
  );
  return match?.key || null;
}

function parseWebhookPayload(payload) {
  if (payload == null) return null;
  if (typeof payload === 'string') {
    const trimmed = payload.trim();
    if (!trimmed) return null;
    try {
      const parsed = JSON.parse(trimmed);
      return parseWebhookPayload(parsed);
    } catch {
      return { text: trimmed };
    }
  }
  if (Array.isArray(payload)) {
    if (payload.length === 0) return null;
    return parseWebhookPayload(payload[0]);
  }
  if (typeof payload === 'object') return payload;
  return null;
}

function extractValidatedDocumentKeyFromWebhook(data) {
  const normalizedData = parseWebhookPayload(data);
  if (!normalizedData || typeof normalizedData !== 'object') return null;

  const textCandidates = [
    normalizedData.documentLabel,
    normalizedData.reason,
    normalizedData.message,
    normalizedData.text,
    normalizedData.output,
    normalizedData.result,
    normalizedData.content?.parts?.map((part) => part?.text).join(' '),
  ].filter(Boolean);

  const candidates = [
    normalizedData.documentKey,
    normalizedData.document_key,
    normalizedData.matchedDocumentKey,
    normalizedData.matched_document_key,
    normalizedData.documentType,
    normalizedData.document_type,
    normalizedData.validatedDocument,
    normalizedData.validated_document,
    ...textCandidates,
  ];
  for (const candidate of candidates) {
    if (!candidate) continue;
    const normalized = normalizeDocText(candidate);
    const exact = REQUIRED_UPLOAD_DOCUMENTS.find((doc) => doc.key === normalized);
    if (exact) return exact.key;
    const detected = detectRequiredDocumentKeyFromText(candidate);
    if (detected) return detected;
  }
  return null;
}

function extractWebhookExpectedFlag(data) {
  const normalizedData = parseWebhookPayload(data);
  if (!normalizedData || typeof normalizedData !== 'object') return null;
  const candidates = [
    normalizedData.isExpected,
    normalizedData.is_expected,
    normalizedData.match,
    normalizedData.matched,
    normalizedData.validation,
    normalizedData.status,
    normalizedData.message,
    normalizedData.text,
    normalizedData.reason,
    normalizedData.content?.parts?.map((part) => part?.text).join(' '),
  ];
  for (const candidate of candidates) {
    if (typeof candidate === 'boolean') return candidate;
    if (typeof candidate === 'string') {
      const normalized = normalizeDocText(candidate);
      if (normalized === 'true' || normalized === 'si' || normalized === 'yes') return true;
      if (normalized === 'false' || normalized === 'no') return false;
      if (
        normalized.includes('valido') ||
        normalized.includes('coincide') ||
        normalized.includes('correcto') ||
        normalized.includes('es valido')
      ) {
        return true;
      }
      if (
        normalized.includes('no valido') ||
        normalized.includes('no coincide') ||
        normalized.includes('incorrecto')
      ) {
        return false;
      }
    }
    if (typeof candidate === 'number') return candidate > 0;
  }
  return null;
}

function toNumberOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : parseFloat(String(value).replace(',', '.'));
  return Number.isNaN(n) ? null : n;
}

/** Cuota máx. → principal (misma idea que el cálculo en BD: ~3,5% y 30 años) para el tip de ingresos. */
function mortgagePrincipalFromPayment(payment, annualRate = 0.035, years = 30) {
  const p = Number(payment);
  if (Number.isNaN(p) || p <= 0) return 0;
  const n = years * 12;
  const r = annualRate / 12;
  if (r <= 0) return p * n;
  const factor = (1 + r) ** n;
  return p * ((factor - 1) / (r * factor));
}

/**
 * Puntos cortos orientados a **subir la nota**, con cifras de clients_affordability + ficha (max_price, ingresos…).
 */
function buildAffordabilityTips(client, affordability, { loading }) {
  if (loading) {
    return { tips: [] };
  }

  const income = toNumberOrNull(client?.income);
  const savings = toNumberOrNull(client?.savings);
  const debts = toNumberOrNull(client?.debts);
  const userMaxPrice = toNumberOrNull(client?.max_price);

  if (income == null || savings == null || debts == null) {
    return {
      tips: [
        'Completa ingresos, ahorros y deudas en tu ficha: sin eso la nota no puede reflejar bien tu situación.',
      ],
    };
  }

  if (!affordability?.raw) {
    return {
      tips: [
        'Cuando exista tu fila en clients_affordability, aquí verás pasos concretos con tus cifras para subir la nota.',
      ],
    };
  }

  const r = affordability.raw;
  const n = (v) => toNumberOrNull(v);
  const priceMax = n(r.price_max);
  const priceBySavings = n(r.price_max_by_savings);
  const savNeedMin = n(r.savings_needed_min);
  const savNeedMax = n(r.savings_needed_max);
  const mpMax = n(r.monthly_payment_max);
  const mpNeeded = n(r.monthly_payment_needed);
  const savingsCalc = n(r.savings);

  const tips = [];
  const add = (t) => {
    if (t && !tips.includes(t)) tips.push(t);
  };

  // 1) Precio objetivo en ficha vs techo del cálculo (impacto directo en la nota)
  if (userMaxPrice != null && userMaxPrice > 0 && priceMax != null) {
    const gap = userMaxPrice - priceMax;
    if (gap > 500) {
      add(
        `Para subir la nota, baja tu precio máximo en la ficha: ahora pones ${formatEuro(userMaxPrice)} y el techo del cálculo es ${formatEuro(priceMax)} (vas unos ${formatEuro(gap)} por encima). Acércalo a ${formatEuro(priceMax)}.`
      );
    } else if (gap <= 500 && gap >= -500) {
      add(
        `Tu precio máximo en ficha (${formatEuro(userMaxPrice)}) ya encaja con el techo del cálculo (${formatEuro(priceMax)}): mantén ahorros y deudas bajo control para no bajar la nota.`
      );
    } else if (gap < -500) {
      add(
        `Tu precio máximo (${formatEuro(userMaxPrice)}) está por debajo del techo calculado (${formatEuro(priceMax)}): el cuello de botella para la nota suele ser otro (ahorro, deudas o ingresos).`
      );
    }
  } else if (priceMax != null) {
    add(
      `Pon en la ficha un precio máximo realista (el cálculo sugiere un techo de unos ${formatEuro(priceMax)}): una meta alineada suele mejorar la puntuación.`
    );
  }

  // 2) Ahorro (entrada / colchón)
  if (savingsCalc != null && savNeedMin != null && savingsCalc + 1 < savNeedMin) {
    const falta = savNeedMin - savingsCalc;
    add(
      `Sube el ahorro disponible: el cálculo pide al menos unos ${formatEuro(savNeedMin)} y marcas unos ${formatEuro(savingsCalc)} (te faltan unos ${formatEuro(falta)}). Eso suele subir la nota.`
    );
  } else if (savNeedMin != null && savNeedMax != null && savNeedMax > savNeedMin) {
    add(
      `Intenta situarte entre ${formatEuro(savNeedMin)} y ${formatEuro(savNeedMax)} de fondo propio: más cerca de ese rango, mejor preparación y nota.`
    );
  } else if (priceBySavings != null && priceMax != null && Math.abs(priceMax - priceBySavings) > 1) {
    add(
      `Ahorrar más sube el techo: solo con ahorro el cálculo llega a unos ${formatEuro(priceBySavings)}; con hipoteca hasta unos ${formatEuro(priceMax)}. Cada ahorro extra cuenta.`
    );
  } else {
    add(
      `Ahorra de forma constante para la entrada y gastos de compra: cada ${formatEuro(1000)} extra de ahorro libre suma capacidad y ayuda a la nota.`
    );
  }

  // 3) Deudas
  if (debts > 0) {
    add(
      `Baja deudas: lo que reduzcas se suma a tu capacidad (1:1 en el modelo típico) y suele mejorar la puntuación.`
    );
  }

  // 4) Ingresos (estimación orientativa con la misma tasa/plazo que el resto del dashboard)
  const loanPerEuroIncome = mortgagePrincipalFromPayment(0.33, 0.035, 30);
  if (income > 0 && loanPerEuroIncome > 0) {
    const gain100 = loanPerEuroIncome * 100;
    add(
      `Si puedes, sube ingresos declarados (o tu situación real): unos ${formatEuro(100)} más al mes suman unos ${formatEuro(gain100)} de capacidad de compra aprox. y eso empuja la nota.`
    );
  }

  // 5) Cuota objetivo
  if (mpNeeded != null && mpMax != null && mpNeeded > mpMax + 1) {
    add(
      `La cuota que encaja con tu precio buscado (unos ${formatEuro(mpNeeded)} / mes) supera el tope orientativo del cálculo (${formatEuro(mpMax)} / mes). Bajar precio de vivienda o mejorar ingresos/ahorro sube la nota.`
    );
  }

  return { tips: tips.slice(0, 6) };
}

function getScoreBand(scoreValue) {
  const v = toNumberOrNull(scoreValue);
  if (v == null) {
    return {
      badge: 'Sin calcular',
      short: 'Rellena ingresos, ahorros y deudas y veremos tu nota al instante.',
      pillClass:
        'bg-muted/80 text-muted-foreground border border-border',
      cardClass:
        'border-border bg-gradient-to-br from-muted/60 to-muted/20',
    };
  }
  if (v >= 85) {
    return {
      badge: 'Muy fuerte',
      short: 'Tu perfil encaja muy bien con una compra parecida a tu objetivo.',
      pillClass:
        'bg-emerald-500/15 text-emerald-800 dark:text-emerald-300 border border-emerald-500/30',
      cardClass:
        'border-emerald-500/40 bg-emerald-500/18 dark:bg-emerald-500/20 bg-gradient-to-br from-emerald-500/28 to-teal-500/18',
    };
  }
  if (v >= 60) {
    return {
      badge: 'Buen camino',
      short: 'Vas bien; unos ajustes en ahorro, deudas o precio pueden subirte aún más.',
      pillClass:
        'bg-teal-500/12 text-teal-900 dark:text-teal-300 border border-teal-500/25',
      cardClass:
        'border-teal-500/30 bg-teal-500/12 dark:bg-teal-500/14 bg-gradient-to-br from-teal-500/18 to-cyan-500/12',
    };
  }
  if (v >= 35) {
    return {
      badge: 'Mejorable',
      short: 'Hay margen: revisa precio objetivo, deudas o ahorro con los consejos de abajo.',
      pillClass:
        'bg-amber-500/12 text-amber-900 dark:text-amber-300 border border-amber-500/25',
      cardClass:
        'border-amber-500/30 bg-amber-500/12 dark:bg-amber-500/14 bg-gradient-to-br from-amber-500/18 to-yellow-500/12',
    };
  }
  return {
    badge: 'A revisar',
    short: 'Tu precio u otras cifras pueden estar frenando la nota; los tips te guían paso a paso.',
    pillClass:
      'bg-orange-500/12 text-orange-900 dark:text-orange-300 border border-orange-500/25',
    cardClass:
      'border-orange-500/30 bg-orange-500/12 dark:bg-orange-500/14 bg-gradient-to-br from-orange-500/18 to-red-500/12',
  };
}

function formatMortgageField(key, value) {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Sí' : 'No';
  const k = key.toLowerCase();
  if (k === 'status') {
    const s = String(value).toLowerCase();
    if (s === 'feasible') return 'Viable (cumple criterios)';
    if (s === 'closest') return 'Aproximada (mejor encaje)';
    return String(value);
  }
  const isMoneyField =
    k.includes('monthly_payment') ||
    k.includes('downpayment') ||
    (k.includes('amount') && !k.includes('percent'));
  if (isMoneyField) {
    const e = formatEuro(value);
    if (e) return e;
  }
  if (k === 'max_ltv') return formatLtv(value) ?? String(value);
  if (k === 'dti') {
    const n = Number(value);
    if (!Number.isNaN(n)) return `${(n * 100).toFixed(2)}% (ratio ${n.toFixed(4)})`;
    return String(value);
  }
  if (k.includes('effort_percent')) {
    const n = Number(value);
    if (!Number.isNaN(n)) return `${n.toFixed(1)}%`;
  }
  if (k.includes('apr')) return formatAprDisplay(value) ?? String(value);
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
}

const MORTGAGE_FIELD_LABELS = {
  bank_name: 'Banco',
  mortgage_name: 'Nombre del producto',
  status: 'Estado',
  monthly_payment_calc: 'Cuota mensual estimada',
  apr: 'TAE / condiciones',
  apr_with_bonus_raw: 'TAE (texto original)',
  max_ltv: 'Financiación máx. (LTV)',
  required_downpayment: 'Entrada necesaria',
  can_pay_downpayment: '¿Puedes afrontar la entrada?',
  missing_downpayment_amount: 'Cantidad que falta ahorrar',
  dti: 'Ratio de esfuerzo (DTI)',
  can_pay_monthly: '¿Cuota asumible?',
  effort_percent: 'Esfuerzo sobre ingresos',
  requires_payroll: 'Requiere nómina',
  requires_insurance_home: 'Seguro del hogar',
  requires_insurance_life: 'Seguro de vida',
  requires_card: 'Vinculación tarjeta',
  requires_pension_plan: 'Plan de pensiones',
  note_short: 'Notas',
};

function humanizeKey(key) {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function MortgageDetailModal({ row, onClose }) {
  useEffect(() => {
    if (!row) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);

    const html = document.documentElement;
    const body = document.body;
    const scrollY = window.scrollY;

    const prev = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyWidth: body.style.width,
    };

    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';

    return () => {
      window.removeEventListener('keydown', onKey);
      html.style.overflow = prev.htmlOverflow;
      body.style.overflow = prev.bodyOverflow;
      body.style.position = prev.bodyPosition;
      body.style.top = prev.bodyTop;
      body.style.left = prev.bodyLeft;
      body.style.right = prev.bodyRight;
      body.style.width = prev.bodyWidth;
      window.scrollTo(0, scrollY);
    };
  }, [row, onClose]);

  const handleBackdrop = useCallback(
    (e) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose]
  );

  if (!row) return null;

  const bank = row.bank_name ?? row.bankName ?? '';
  const name = row.mortgage_name ?? row.mortgageName ?? '';
  const status = (row.status ?? '').toString().toLowerCase();
  const isFeasible = status === 'feasible';
  const note = row.note_short != null ? String(row.note_short) : '';

  const primaryKeys = new Set([
    'bank_name',
    'bankName',
    'mortgage_name',
    'mortgageName',
    'status',
    'monthly_payment_calc',
    'apr',
    'apr_with_bonus_raw',
    'max_ltv',
    'required_downpayment',
    'can_pay_downpayment',
    'missing_downpayment_amount',
    'dti',
    'can_pay_monthly',
    'effort_percent',
    'requires_payroll',
    'requires_insurance_home',
    'requires_insurance_life',
    'requires_card',
    'requires_pension_plan',
    'note_short',
  ]);

  const orderedKeys = [
    'status',
    'monthly_payment_calc',
    'apr',
    'apr_with_bonus_raw',
    'max_ltv',
    'required_downpayment',
    'can_pay_downpayment',
    'missing_downpayment_amount',
    'dti',
    'effort_percent',
    'can_pay_monthly',
    'requires_payroll',
    'requires_insurance_home',
    'requires_insurance_life',
    'requires_card',
    'requires_pension_plan',
  ];

  const extraEntries = Object.entries(row).filter(([k]) => !primaryKeys.has(k));

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-hidden overscroll-none"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mortgage-detail-title"
    >
      <button
        type="button"
        className="absolute inset-0 z-0 bg-black/50 backdrop-blur-sm border-0 cursor-default"
        onClick={handleBackdrop}
        aria-label="Cerrar"
      />
      <div
        className="relative z-10 w-full sm:max-w-lg max-h-[92vh] sm:max-h-[85vh] flex flex-col rounded-t-3xl sm:rounded-3xl border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={`h-1.5 rounded-t-3xl shrink-0 ${
            isFeasible ? 'bg-gradient-to-r from-emerald-500 to-teal-500' : 'bg-gradient-to-r from-amber-500 to-orange-500'
          }`}
        />
        <div className="flex items-start justify-between gap-3 p-5 sm:p-6 border-b border-border/80">
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Hipoteca</p>
            <h2 id="mortgage-detail-title" className="text-xl sm:text-2xl font-bold text-foreground break-words">
              {bank || 'Banco'}
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground mt-2 break-words leading-relaxed">{name || '—'}</p>
            <span
              className={`inline-block mt-3 text-xs font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full ${
                isFeasible
                  ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
                  : 'bg-amber-500/15 text-amber-800 dark:text-amber-400'
              }`}
            >
              {isFeasible ? 'Viable' : 'Aproximada'}
            </span>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="shrink-0 rounded-full h-10 w-10"
            onClick={onClose}
            aria-label="Cerrar detalle"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="overflow-y-auto overscroll-contain flex-1 min-h-0 px-5 sm:px-6 py-4 space-y-6 touch-pan-y">
          <dl className="space-y-0 divide-y divide-border/60">
            {orderedKeys.map((key) => {
              if (!(key in row)) return null;
              if (
                key === 'apr_with_bonus_raw' &&
                row.apr &&
                String(row.apr).trim() === String(row.apr_with_bonus_raw ?? '').trim()
              ) {
                return null;
              }
              const label = MORTGAGE_FIELD_LABELS[key] || humanizeKey(key);
              const val = formatMortgageField(key, row[key]);
              return (
                <div key={key} className="py-3.5 first:pt-0 grid gap-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] sm:gap-4">
                  <dt className="text-sm text-muted-foreground">{label}</dt>
                  <dd className="text-sm font-medium text-foreground break-words whitespace-pre-wrap">{val}</dd>
                </div>
              );
            })}
          </dl>

          {note && (
            <div className="rounded-2xl border border-border/80 bg-muted/30 p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <FileText className="w-4 h-4 shrink-0" />
                <span className="text-xs font-semibold uppercase tracking-wide">Notas</span>
              </div>
              <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap break-words">{note}</p>
            </div>
          )}

          {extraEntries.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Más datos</p>
              <dl className="space-y-0 divide-y divide-border/40 rounded-xl border border-border/50 bg-muted/20 p-3">
                {extraEntries.map(([key, value]) => (
                  <div
                    key={key}
                    className="py-2.5 first:pt-0 grid gap-1 sm:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] sm:gap-3"
                  >
                    <dt className="text-xs text-muted-foreground">
                      {MORTGAGE_FIELD_LABELS[key] || humanizeKey(key)}
                    </dt>
                    <dd className="text-xs font-medium break-all sm:break-words whitespace-pre-wrap">
                      {formatMortgageField(key, value)}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </div>

        <div className="p-4 sm:p-5 border-t border-border/80 shrink-0">
          <Button type="button" variant="hero" className="w-full rounded-full" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      </div>
    </div>
  );
}

function ProfileStat({ icon: Icon, label, value, sub, hint }) {
  return (
    <div className="rounded-xl border border-border/60 bg-background/60 px-3 py-3 sm:px-4 shadow-sm hover:border-primary/20 transition-colors">
      <div className="flex items-center gap-2 text-muted-foreground mb-1">
        {Icon && <Icon className="w-3.5 h-3.5 shrink-0 opacity-80" />}
        <span className="text-[11px] sm:text-xs font-medium uppercase tracking-wide">{label}</span>
      </div>
      <p className="text-sm sm:text-base font-semibold text-foreground tabular-nums break-words">{value}</p>
      {hint && <p className="text-[10px] text-muted-foreground mt-1 leading-snug">{hint}</p>}
      {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}

function UserFinancialProfile({
  client,
  loading,
  affordability,
  affordabilityLoading,
  isEditing,
  isSaving,
  editData,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onInputChange,
}) {
  if (loading) {
    return (
      <div className="glass rounded-3xl border border-primary/15 p-6 sm:p-8 shadow-sm dark:shadow-xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-xl bg-muted animate-pulse" />
          <div className="space-y-2 flex-1">
            <div className="h-5 w-48 bg-muted rounded animate-pulse" />
            <div className="h-4 w-full max-w-md bg-muted/70 rounded animate-pulse" />
          </div>
        </div>
        <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-20 rounded-xl bg-muted/50 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="glass rounded-3xl border border-dashed border-primary/25 p-8 text-center space-y-4">
        <div className="w-14 h-14 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center">
          <User className="w-7 h-7 text-primary" />
        </div>
        <div>
          <p className="text-base font-semibold text-foreground">Aún no tenemos tu ficha</p>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto leading-relaxed">
            Cuando completes tus datos podremos calcular tu puntuación y enseñarte hipotecas a tu medida.
          </p>
        </div>
        <Button variant="hero" className="rounded-full" asChild>
          <Link to="/dashboard">Completar mis datos</Link>
        </Button>
      </div>
    );
  }

  const c = client;
  const profile = isEditing ? (editData || c) : c;
  const scoreValue = toNumberOrNull(c.housing_readiness_score);
  const hasMaxScore = scoreValue != null && scoreValue >= 100;
  const { tips: insightTips } = buildAffordabilityTips(c, affordability, {
    loading: affordabilityLoading,
  });
  const scoreBand = getScoreBand(c.housing_readiness_score);

  return (
    <div className="glass rounded-3xl border border-primary/15 p-5 sm:p-6 shadow-sm dark:shadow-xl overflow-hidden relative">
      <div className="absolute -right-16 -top-16 w-48 h-48 rounded-full bg-primary/5 blur-3xl pointer-events-none" />
      <div className="relative">
        <div className="flex items-start gap-3 mb-6">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 ring-1 ring-primary/20">
            <User className="w-7 h-7 text-primary" />
          </div>
          <div>
            <h2 className="text-xl sm:text-2xl font-bold tracking-tight">Tu preparación para comprar</h2>
            <p className="text-sm text-muted-foreground mt-0.5 leading-relaxed">
              Resumen de lo que usamos para tu puntuación y tus hipotecas. Solo tú lo ves.
            </p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {isEditing ? (
              <>
                <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={onCancelEdit}>
                  Cancelar
                </Button>
                <Button
                  type="button"
                  variant="hero"
                  size="sm"
                  className="rounded-full"
                  onClick={onSaveEdit}
                  disabled={isSaving}
                >
                  {isSaving ? 'Guardando...' : 'Guardar'}
                </Button>
              </>
            ) : (
              <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={onStartEdit}>
                Editar
              </Button>
            )}
          </div>
        </div>
        {isEditing && (
          <div className="mb-4 rounded-xl border border-primary/30 bg-primary/[0.08] px-3 py-2">
            <p className="text-xs font-medium text-primary">
              Campos editables resaltados
            </p>
          </div>
        )}

        <div className="mb-6">
          <div className={`rounded-2xl border p-4 sm:p-6 ${scoreBand.cardClass}`}>
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-primary/80">
                Tu puntuación
              </p>
              <span
                className={`self-start text-xs font-semibold px-2.5 py-1 rounded-full ${scoreBand.pillClass}`}
              >
                {scoreBand.badge}
              </span>
            </div>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
              <div>
                <p className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight tabular-nums text-foreground">
                  {scoreValue != null ? Number(scoreValue).toFixed(2) : '—'}
                  <span className="text-xl sm:text-2xl font-semibold text-muted-foreground ml-2">de 100</span>
                </p>
                <p className="text-sm text-foreground/90 mt-2 leading-relaxed max-w-xl">{scoreBand.short}</p>
                <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                  La nota resume ingresos, ahorro neto, deudas y el precio que buscas.
                </p>
              </div>
            </div>
          </div>
        </div>

        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
          Números clave
        </p>
        <div className="mb-6 grid gap-3 sm:gap-4 2xl:gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          <ProfileStat
            label="Hasta dónde llegarías (aprox.)"
            hint="Según el último cálculo guardado para tu email (precio máximo)."
            value={
              affordabilityLoading ? (
                <span className="inline-block h-6 w-24 rounded bg-muted animate-pulse align-middle" />
              ) : (
                displayOrDash(affordability?.totalCapacity, formatEuro)
              )
            }
            icon={Gauge}
          />
          <ProfileStat
            label="Tope de cuota al mes (33%)"
            hint="Cuota mensual del mismo cálculo (rango o valor estimado)."
            value={
              affordabilityLoading ? (
                <span className="inline-block h-6 w-20 rounded bg-muted animate-pulse align-middle" />
              ) : affordability?.maxMonthlyPayment33 != null ? (
                `${formatEuro(affordability.maxMonthlyPayment33) ?? '—'} / mes`
              ) : (
                '—'
              )
            }
            icon={Activity}
          />
          <ProfileStat
            label="Ingresos al mes"
            value={displayOrDash(c.income, formatEuro)}
            icon={Euro}
          />
          <ProfileStat
            label="Ahorro libre"
            hint="Ahorros menos deudas."
            value={displayOrDash(
              toNumberOrNull(c.savings) != null && toNumberOrNull(c.debts) != null
                ? Math.max(toNumberOrNull(c.savings) - toNumberOrNull(c.debts), 0)
                : null,
              formatEuro
            )}
            icon={PiggyBank}
          />
        </div>

        <details className="mb-5 group rounded-2xl border border-primary/25 bg-background/40 open:bg-background/60 hover:border-primary/40 transition-colors">
          <summary className="list-none cursor-pointer select-none px-4 sm:px-5 py-3.5 flex items-center justify-between gap-3 rounded-2xl bg-primary/[0.05] hover:bg-primary/[0.09] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background">
            <span className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2 min-w-0 text-left">
              <span className="text-sm font-semibold tracking-wide flex items-center gap-2 shrink-0 min-w-0">
                <Home className="w-4 h-4 text-primary shrink-0" />
                Casa que buscas
              </span>
              <span className="text-[11px] text-primary/80 font-medium sm:ml-1">
                Pulsa para desplegar
              </span>
            </span>

            <span className="flex items-center gap-2 min-w-0">
              <span className="hidden sm:inline-flex items-center rounded-full border border-primary/25 bg-primary/10 text-primary text-[10px] font-semibold px-2 py-1">
                Desplegar
              </span>
              <span className="flex flex-wrap items-center gap-2 justify-end text-xs text-muted-foreground min-w-0">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/10 px-2 py-1 text-primary max-w-[min(100%,280px)] sm:max-w-[min(100%,320px)]">
                  <Euro className="w-3.5 h-3.5 text-primary" />
                  <span className="truncate">
                    Precio: {displayOrDash(profile.max_price, formatEuro)}
                  </span>
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-background/70 px-2 py-1 max-w-[min(100%,360px)] sm:max-w-[min(100%,420px)]">
                  <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="truncate">
                    Ubicación: {displayOrDash(profile.location)}
                  </span>
                </span>
              </span>
              <ChevronRight className="w-4 h-4 text-primary transition-transform group-open:rotate-90 shrink-0" />
            </span>
          </summary>
          <div className="px-4 sm:px-5 pb-4 sm:pb-5 border-t border-border/50">
            <div className="pt-4 grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {isEditing ? (
                <>
                  <div className="rounded-xl border border-primary/30 bg-primary/[0.06] p-2.5">
                    <p className="text-xs text-primary/90 font-semibold mb-1">Precio objetivo (máximo)</p>
                    <Select
                      value={profile.max_price ?? ''}
                      onChange={(val) => onInputChange('max_price', val)}
                      options={[
                        { value: '', label: 'No especificado' },
                        ...maxPriceOptions.map((v) => ({ value: v, label: formatEuro(v) })),
                      ]}
                      placeholder="No especificado"
                      className="border-primary/40"
                      forceTop={true}
                    />
                  </div>
                  <div className="rounded-xl border border-primary/30 bg-primary/[0.06] p-2.5">
                    <p className="text-xs text-primary/90 font-semibold mb-1">Precio mínimo</p>
                    <Select
                      value={profile.min_price ?? ''}
                      onChange={(val) => onInputChange('min_price', val)}
                      options={[
                        { value: '', label: 'No especificado' },
                        ...minPriceOptions.map((v) => ({ value: v, label: formatEuro(v) })),
                      ]}
                      placeholder="No especificado"
                      className="border-primary/40"
                      forceTop={true}
                    />
                  </div>
                  <div className="rounded-xl border border-primary/30 bg-primary/[0.06] p-2.5">
                    <p className="text-xs text-primary/90 font-semibold mb-1">Habitaciones</p>
                    <Select
                      value={profile.bedrooms ?? ''}
                      onChange={(val) => onInputChange('bedrooms', val)}
                      options={[
                        { value: '', label: 'No especificado' },
                        ...bedroomsOptions.map((v) => ({ value: v, label: String(v) })),
                      ]}
                      placeholder="No especificado"
                      className="border-primary/40"
                      forceTop={true}
                    />
                  </div>
                  <div className="rounded-xl border border-primary/30 bg-primary/[0.06] p-2.5">
                    <p className="text-xs text-primary/90 font-semibold mb-1">Baños</p>
                    <Select
                      value={profile.bathrooms ?? ''}
                      onChange={(val) => onInputChange('bathrooms', val)}
                      options={[
                        { value: '', label: 'No especificado' },
                        ...bathroomsOptions.map((v) => ({ value: v, label: String(v) })),
                      ]}
                      placeholder="No especificado"
                      className="border-primary/40"
                      forceTop={true}
                    />
                  </div>
                  <div className="rounded-xl border border-primary/30 bg-primary/[0.06] p-2.5">
                    <p className="text-xs text-primary/90 font-semibold mb-1">Tamaño mínimo (m²)</p>
                    <Select
                      value={profile.min_size ?? ''}
                      onChange={(val) => onInputChange('min_size', val)}
                      options={[
                        { value: '', label: 'No especificado' },
                        ...minSizeOptions.map((v) => ({ value: v, label: `${v} m²` })),
                      ]}
                      placeholder="No especificado"
                      className="border-primary/40"
                      forceTop={true}
                    />
                  </div>
                  <div className="rounded-xl border border-border/60 bg-muted/20 p-2.5">
                    <p className="text-xs text-muted-foreground mb-1">Ubicación</p>
                    <Input value={profile.location ?? ''} disabled />
                  </div>
                </>
              ) : (
                <>
                  <ProfileStat label="Precio objetivo (máximo)" value={displayOrDash(profile.max_price, formatEuro)} />
                  <ProfileStat label="Precio mínimo" value={displayOrDash(profile.min_price, formatEuro)} />
                  <ProfileStat label="Habitaciones" value={displayOrDash(profile.bedrooms)} />
                  <ProfileStat label="Baños" value={displayOrDash(profile.bathrooms)} />
                  <ProfileStat label="Tamaño mínimo (m²)" value={displayOrDash(profile.min_size)} />
                  <ProfileStat
                    label="Ubicación"
                    value={displayOrDash(profile.location)}
                  />
                </>
              )}
            </div>
          </div>
        </details>

        <div className="mb-5 rounded-2xl border border-border/70 bg-background/40 p-4 sm:p-5">
          {hasMaxScore ? (
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                Puntuación al máximo
              </p>
              <div className="rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/12 via-teal-500/8 to-background p-4 sm:p-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center shrink-0">
                  <Sparkles className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1.5">
                    <p className="text-sm font-semibold text-foreground">Preparación excelente</p>
                    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30">
                      100 / 100
                    </span>
                  </div>
                  <p className="text-sm text-foreground/90 leading-relaxed">
                    Tienes la puntuación máxima. Mantén este nivel y revisa periódicamente tus datos para conservar
                    esta posición.
                  </p>
                </div>
              </div>
            </div>
            </div>
          ) : (
            <>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                Cómo subir tu nota
              </p>
              <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
                Cada punto es una acción que suele subir la puntuación, con tus cifras y el último cálculo guardado.
              </p>
              <ul className="space-y-2.5 text-sm text-foreground list-none pl-0">
                {affordabilityLoading ? (
                  <>
                    {[1, 2, 3].map((i) => (
                      <li
                        key={i}
                        className="flex items-start gap-3 rounded-xl border border-border/50 bg-background/50 px-3 py-2.5"
                      >
                        <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-muted animate-pulse" />
                        <span className="flex-1 h-10 rounded-lg bg-muted animate-pulse" />
                      </li>
                    ))}
                  </>
                ) : (
                  insightTips.map((tip, idx) => (
                    <li
                      key={idx}
                      className="flex items-start gap-3 rounded-xl border border-border/50 bg-background/50 px-3 py-2.5"
                    >
                      <span
                        className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary ring-4 ring-primary/15"
                        aria-hidden
                      />
                      <span className="leading-relaxed">{tip}</span>
                    </li>
                  ))
                )}
              </ul>
            </>
          )}
        </div>

        <details className="group rounded-2xl border border-primary/25 bg-background/40 open:bg-background/60 hover:border-primary/40 transition-colors">
          <summary className="list-none cursor-pointer select-none px-4 sm:px-5 py-3.5 flex items-center justify-between gap-3 rounded-2xl bg-primary/[0.05] hover:bg-primary/[0.09] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background">
            <span className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2 min-w-0 text-left">
              <span className="text-sm font-semibold tracking-wide flex items-center gap-2">
                <User className="w-4 h-4 text-primary shrink-0" />
                Todos tus datos
              </span>
              <span className="text-[11px] text-primary/80 font-medium sm:ml-1">
                Identidad, dinero, vivienda y trabajo
              </span>
            </span>
            <span className="flex items-center gap-2">
              <span className="hidden sm:inline-flex items-center rounded-full border border-primary/25 bg-primary/10 text-primary text-[10px] font-semibold px-2 py-1">
                Desplegar
              </span>
              <ChevronRight className="w-4 h-4 text-primary transition-transform group-open:rotate-90 shrink-0" />
            </span>
          </summary>
          <div className="px-4 sm:px-5 pb-4 sm:pb-5 space-y-5 border-t border-border/50">
            <div className="pt-4">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
                <User className="w-3.5 h-3.5" />
                Identificación
              </p>
              <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {isEditing ? (
                  <>
                    <div className="rounded-xl border border-primary/30 bg-primary/[0.06] p-2.5">
                      <p className="text-xs text-primary/90 font-semibold mb-1">Nombre</p>
                      <Input
                        value={profile.name ?? ''}
                        onChange={(e) => onInputChange('name', e.target.value)}
                        className="border-primary/40"
                      />
                    </div>
                    <div className="rounded-xl border border-border/60 bg-muted/20 p-2.5">
                      <p className="text-xs text-muted-foreground mb-1">Email</p>
                      <Input value={profile.email ?? ''} disabled />
                    </div>
                    <div className="rounded-xl border border-primary/30 bg-primary/[0.06] p-2.5">
                      <p className="text-xs text-primary/90 font-semibold mb-1">Teléfono</p>
                      <Input
                        value={profile.phone_number ?? ''}
                        onChange={(e) => onInputChange('phone_number', e.target.value)}
                        maxLength={15}
                        className="border-primary/40"
                      />
                    </div>
                    <div className="rounded-xl border border-primary/30 bg-primary/[0.06] p-2.5">
                      <p className="text-xs text-primary/90 font-semibold mb-1">Edad</p>
                      <Input
                        type="number"
                        value={profile.age ?? ''}
                        onChange={(e) => onInputChange('age', e.target.value)}
                        min={18}
                        max={100}
                        className="border-primary/40"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <ProfileStat label="Nombre" value={displayOrDash(profile.name)} icon={User} />
                    <ProfileStat label="Email" value={displayOrDash(profile.email)} />
                    <ProfileStat label="Teléfono" value={displayOrDash(profile.phone_number)} />
                    <ProfileStat label="Edad" value={displayOrDash(profile.age)} />
                  </>
                )}
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
                <Wallet className="w-3.5 h-3.5" />
                Situación financiera
              </p>
              <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                <ProfileStat
                  label="Ingresos mensuales"
                  value={displayOrDash(c.income, formatEuro)}
                  icon={Euro}
                />
                <ProfileStat label="Ahorros" value={displayOrDash(c.savings, formatEuro)} icon={PiggyBank} />
                <ProfileStat label="Deudas" value={displayOrDash(c.debts, formatEuro)} />
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
                <Home className="w-3.5 h-3.5" />
                Vivienda que buscas
              </p>
              <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                <ProfileStat label="Precio máximo" value={displayOrDash(profile.max_price, formatEuro)} />
                <ProfileStat label="Precio mínimo" value={displayOrDash(profile.min_price, formatEuro)} />
                <ProfileStat
                  label="Superficie mín. (m²)"
                  value={displayOrDash(profile.min_size)}
                />
                <ProfileStat
                  label="Habitaciones / baños"
                  value={
                    profile.bedrooms != null || profile.bathrooms != null
                      ? `${profile.bedrooms ?? '—'} hab. · ${profile.bathrooms ?? '—'} baños`
                      : '—'
                  }
                />
                <ProfileStat
                  label="Ubicación"
                  value={displayOrDash(profile.location)}
                />
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
                <Briefcase className="w-3.5 h-3.5" />
                Empleo
              </p>
              <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                <ProfileStat label="Tipo" value={displayOrDash(c.employment_type)} />
                <ProfileStat
                  label="Antigüedad (años)"
                  value={displayOrDash(c.employment_years)}
                />
              </div>
            </div>
          </div>
        </details>
      </div>
    </div>
  );
}

function MortgageRecommendationCard({ row, onOpen }) {
  const bank = row.bank_name ?? row.bankName;
  const name = row.mortgage_name ?? row.mortgageName;
  const status = (row.status ?? '').toString().toLowerCase();
  const isFeasible = status === 'feasible';

  const monthly = formatEuro(row.monthly_payment_calc);
  const apr = formatAprDisplay(row.apr ?? row.apr_with_bonus_raw);
  const ltv = formatLtv(row.max_ltv);
  const effort =
    row.effort_percent != null && row.effort_percent !== ''
      ? `${Number(row.effort_percent).toFixed(1)}%`
      : row.dti != null
        ? `${(Number(row.dti) * 100).toFixed(1)}%`
        : null;

  const canPayDown = row.can_pay_downpayment;
  const missing = row.missing_downpayment_amount;
  const required = formatEuro(row.required_downpayment);
  const note = row.note_short ? String(row.note_short).trim() : '';

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen(row);
    }
  };

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={() => onOpen(row)}
      onKeyDown={handleKeyDown}
      className={`
        group relative flex flex-col rounded-2xl border transition-all duration-300
        bg-gradient-to-br from-card via-card to-primary/[0.03]
        border-border/80 hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5
        cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background
        active:scale-[0.99]
      `}
    >
      <div
        className={`
          absolute inset-x-0 top-0 h-1 rounded-t-2xl
          ${isFeasible ? 'bg-gradient-to-r from-emerald-500/80 to-teal-500/70' : 'bg-gradient-to-r from-amber-500/70 to-orange-500/60'}
        `}
      />

      <div className="p-5 pt-6 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-2 mb-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-primary/90 mb-1">
              <Building2 className="w-4 h-4 shrink-0" />
              <span className="font-semibold text-lg leading-snug break-words">{bank || 'Banco'}</span>
            </div>
            <p className="text-sm text-muted-foreground leading-snug break-words">{name || 'Producto hipotecario'}</p>
          </div>
          <span
            className={`
              shrink-0 text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full
              ${isFeasible ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400' : 'bg-amber-500/15 text-amber-800 dark:text-amber-400'}
            `}
          >
            {isFeasible ? 'Viable' : 'Aproximada'}
          </span>
        </div>

        <div className="rounded-xl bg-muted/40 border border-border/50 p-4 mb-4">
          <p className="text-xs font-medium text-muted-foreground mb-1">Cuota mensual estimada</p>
          <p className="text-2xl md:text-3xl font-bold tracking-tight text-foreground tabular-nums">
            {monthly ?? '—'}
            <span className="text-sm font-normal text-muted-foreground ml-1">/mes</span>
          </p>
        </div>

        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="rounded-lg bg-background/60 border border-border/60 px-2 py-2.5 text-center">
            <Percent className="w-3.5 h-3.5 mx-auto mb-1 text-primary/70" />
            <p className="text-[10px] text-muted-foreground uppercase tracking-wide">TAE</p>
            <p className="text-sm font-semibold tabular-nums break-words">{apr ?? '—'}</p>
          </div>
          <div className="rounded-lg bg-background/60 border border-border/60 px-2 py-2.5 text-center">
            <Landmark className="w-3.5 h-3.5 mx-auto mb-1 text-primary/70" />
            <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Financiación</p>
            <p className="text-sm font-semibold tabular-nums">{ltv ?? '—'}</p>
          </div>
          <div className="rounded-lg bg-background/60 border border-border/60 px-2 py-2.5 text-center">
            <Activity className="w-3.5 h-3.5 mx-auto mb-1 text-primary/70" />
            <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Esfuerzo</p>
            <p className="text-sm font-semibold tabular-nums">{effort ?? '—'}</p>
          </div>
        </div>

        <div className="mt-auto space-y-2 text-sm border-t border-border/50 pt-4">
          {required && (
            <div className="flex items-center justify-between gap-2 text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <Euro className="w-3.5 h-3.5" />
                Entrada necesaria
              </span>
              <span className="font-medium text-foreground tabular-nums">{required}</span>
            </div>
          )}
          {canPayDown === false && missing != null && Number(missing) > 0 && (
            <div className="flex items-center justify-between gap-2 rounded-lg bg-amber-500/10 px-2 py-1.5 text-amber-900 dark:text-amber-200">
              <span className="flex items-center gap-1.5">
                <PiggyBank className="w-3.5 h-3.5" />
                Falta ahorrar
              </span>
              <span className="font-semibold tabular-nums">{formatEuro(missing)}</span>
            </div>
          )}
          {canPayDown === true && (
            <p className="text-xs text-emerald-700/90 dark:text-emerald-400 flex items-center gap-1">
              <Sparkles className="w-3.5 h-3.5" />
              Entrada cubierta con tu perfil de ahorro
            </p>
          )}
          {note && (
            <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed pt-1">{note}</p>
          )}
          <p className="text-xs font-medium text-primary flex items-center gap-1 mt-3 pt-2 border-t border-border/40">
            Pulsa para ver condiciones y notas
            <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
          </p>
        </div>
      </div>
    </article>
  );
}

function formatChatDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  } catch {
    return '';
  }
}

function UserChatsSidebar({
  chats,
  loading,
  onOpenChat,
  onRenameChat,
  onDeleteChat,
  reportFiles,
  setReportFiles,
  reportUploadError,
  reportUploadSuccess,
  uploadingReportFile,
  onUploadReportFile,
  uploadedDocumentsCount,
  documentsRequiredTotal,
  validatedDocumentKeys,
}) {
  const reportFileInputRef = useRef(null);
  const [chatToRename, setChatToRename] = useState(null);
  const [newChatName, setNewChatName] = useState('');
  const [busyChatId, setBusyChatId] = useState(null);
  const [chatToDelete, setChatToDelete] = useState(null);

  const startRename = (chat) => {
    setChatToRename(chat.id);
    setNewChatName(chat.chat_name || '');
  };

  const cancelRename = () => {
    setChatToRename(null);
    setNewChatName('');
  };

  const confirmRename = async (chatId) => {
    const trimmed = newChatName.trim();
    if (!trimmed) return;
    setBusyChatId(chatId);
    try {
      await onRenameChat(chatId, trimmed);
      cancelRename();
    } finally {
      setBusyChatId(null);
    }
  };

  const requestDelete = (chatId) => {
    setChatToDelete(chatId);
  };

  const confirmDelete = async () => {
    if (!chatToDelete) return;
    const chatId = chatToDelete;
    setBusyChatId(chatId);
    try {
      await onDeleteChat(chatId);
      if (chatToRename === chatId) {
        cancelRename();
      }
      setChatToDelete(null);
    } catch (error) {
      // Evita romper el flujo de UI si falla la petición.
    } finally {
      setBusyChatId(null);
    }
  };

  const clearSelectedReportFile = (indexToRemove) => {
    setReportFiles((prev) => {
      const next = prev.filter((_, idx) => idx !== indexToRemove);
      if (next.length === 0 && reportFileInputRef.current) {
        reportFileInputRef.current.value = '';
      }
      return next;
    });
  };

  useEffect(() => {
    if ((reportFiles?.length || 0) === 0 && reportFileInputRef.current) {
      reportFileInputRef.current.value = '';
    }
  }, [reportFiles]);

  const handlePickReportFiles = (e) => {
    const pickedFiles = Array.from(e.target.files || []);
    if (!pickedFiles.length) return;

    setReportFiles((prev) => {
      const existingSignatures = new Set(
        prev.map((file) => `${file.name}-${file.size}-${file.lastModified}`)
      );
      const filesToAdd = pickedFiles.filter(
        (file) => !existingSignatures.has(`${file.name}-${file.size}-${file.lastModified}`)
      );
      return [...prev, ...filesToAdd];
    });

    // Limpia el input para poder volver a elegir el mismo archivo si hace falta.
    e.target.value = '';
  };

  const normalizedUploaded = Math.max(0, uploadedDocumentsCount || 0);
  const normalizedRequired = Math.max(1, documentsRequiredTotal || 1);
  const uploadedCapped = Math.min(normalizedUploaded, normalizedRequired);
  const remainingDocuments = Math.max(normalizedRequired - uploadedCapped, 0);
  const remainingPercent = Math.max(100 - Math.round((uploadedCapped / normalizedRequired) * 100), 0);
  const completedPercent = Math.min(100, 100 - remainingPercent);

  return (
    <>
    <div className="flex flex-col gap-4 h-full min-h-0">
      <div className="glass rounded-3xl border border-primary/20 shadow-sm dark:shadow-xl overflow-hidden relative flex flex-col min-h-0 lg:flex-[1_1_0]">
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-violet-500/80 via-primary/70 to-cyan-500/70" />
      <div className="p-4 sm:p-5 pt-5 flex flex-col flex-1 min-h-0">
        <div className="flex items-start gap-3 mb-4 shrink-0">
          <div className="p-2.5 rounded-2xl bg-gradient-to-br from-violet-500/20 to-primary/15 ring-1 ring-primary/25">
            <Bot className="w-6 h-6 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-bold tracking-tight">Chats con GABI</h3>
            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
              Aquí están tus conversaciones. Elige una para seguir hablando justo donde la dejaste.
            </p>
          </div>
        </div>

        <Button
          type="button"
          variant="hero"
          className="w-full rounded-2xl h-11 gap-2 shadow-sm dark:shadow-lg dark:shadow-primary/15 mb-4 shrink-0"
          asChild
        >
          <Link to="/chat">
            <Plus className="w-4 h-4" />
            Hablar con GABI
          </Link>
        </Button>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain pr-1 -mr-1 space-y-2">
          {loading && (
            <div className="space-y-2">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="h-16 rounded-2xl bg-muted/50 animate-pulse border border-border/40"
                />
              ))}
            </div>
          )}

          {!loading && chats.length === 0 && (
            <div className="text-center py-10 px-3 rounded-2xl border border-dashed border-border/80 bg-muted/20">
              <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-gradient-to-br from-primary/20 to-violet-500/10 flex items-center justify-center">
                <MessageSquare className="w-7 h-7 text-primary/70" />
              </div>
              <p className="text-sm font-medium text-foreground">Todavía no hay chats</p>
              <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                Pulsa el botón de arriba para empezar: cuando escribas, guardamos la conversación aquí.
              </p>
            </div>
          )}

          {!loading &&
            chats.map((chat) => (
              <div
                key={chat.id}
                className="group w-full text-left rounded-2xl border border-border/60 bg-background/50 hover:bg-primary/[0.07] hover:border-primary/30 px-3 py-3 transition-all duration-200"
              >
                {chatToRename === chat.id ? (
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={newChatName}
                      onChange={(e) => setNewChatName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          void confirmRename(chat.id);
                        }
                        if (e.key === 'Escape') {
                          e.preventDefault();
                          cancelRename();
                        }
                      }}
                      className="w-full h-9 rounded-lg border border-border bg-background px-3 text-sm outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-primary"
                      placeholder="Nuevo nombre del chat"
                      autoFocus
                    />
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-8 px-2"
                        onClick={cancelRename}
                        disabled={busyChatId === chat.id}
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="hero"
                        className="h-8 px-2"
                        onClick={() => void confirmRename(chat.id)}
                        disabled={busyChatId === chat.id || !newChatName.trim()}
                      >
                        <Check className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => onOpenChat(chat.id)}
                      aria-label={`Abrir chat: ${chat.chat_name || 'Sin título'}`}
                      className="flex-1 min-w-0 flex items-center gap-3"
                    >
                      <div className="shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-primary/25 to-violet-500/15 flex items-center justify-center ring-1 ring-primary/20 group-hover:scale-[1.02] transition-transform">
                        <MessageSquare className="w-4 h-4 text-primary" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold truncate text-foreground group-hover:text-primary transition-colors">
                          {chat.chat_name || 'Chat sin título'}
                        </p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                          {chat.updated_at ? `Actualizado ${formatChatDate(chat.updated_at)}` : 'Sin fecha'}
                        </p>
                      </div>
                    </button>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => startRename(chat)}
                        disabled={busyChatId === chat.id}
                        aria-label="Renombrar chat"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => requestDelete(chat.id)}
                        disabled={busyChatId === chat.id}
                        aria-label="Eliminar chat"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
        </div>
      </div>
      </div>

      <form
        onSubmit={onUploadReportFile}
        className="glass rounded-3xl border border-primary/20 shadow-sm dark:shadow-xl p-4 sm:p-5 flex flex-col gap-3 lg:flex-[1_1_0] justify-between"
      >
        <div className="flex items-start gap-3">
          <div className="p-2.5 rounded-2xl bg-gradient-to-br from-primary/20 to-indigo-500/10 ring-1 ring-primary/25">
            <FileText className="w-6 h-6 text-primary" />
          </div>
          <div className="min-w-0">
            <h3 className="text-lg font-bold tracking-tight">Subir Archivos</h3>
            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
              Analiza documentos con IA desde el dashboard.
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <details className="rounded-xl border border-primary/20 bg-primary/[0.04]">
            <summary className="cursor-pointer list-none flex items-center justify-between gap-2 px-3 py-2.5 text-xs font-semibold text-foreground">
              <span>Ver qué archivos subir</span>
              <span className="text-[11px] text-primary">Documentación recomendada</span>
            </summary>
            <div className="px-3 pb-3">
              <ul className="space-y-1.5 text-[11px] text-muted-foreground leading-relaxed">
                {REQUIRED_UPLOAD_DOCUMENTS.map((doc) => {
                  const isValidated = validatedDocumentKeys.includes(doc.key);
                  return (
                    <li
                      key={doc.key}
                      className={isValidated ? 'line-through text-emerald-600 dark:text-emerald-400' : ''}
                    >
                      {isValidated ? '✓ ' : ''}
                      {doc.label}
                    </li>
                  );
                })}
              </ul>
            </div>
          </details>
          <div className="rounded-xl border border-border/60 bg-background/60 p-2.5">
            <div className="flex items-center justify-between text-[11px] mb-1">
              <span className="text-muted-foreground">Subidos: {uploadedCapped}/{normalizedRequired}</span>
              <span className="text-primary font-semibold">Faltan: {remainingDocuments} ({remainingPercent}%)</span>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-primary to-indigo-500 transition-all duration-500"
                style={{ width: `${completedPercent}%` }}
              />
            </div>
          </div>
          <Input
            ref={reportFileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,application/pdf,image/jpeg"
            onChange={handlePickReportFiles}
            className="text-xs"
          />
          <Button
            type="submit"
            variant="outline"
            className="w-full rounded-full"
            disabled={uploadingReportFile || !reportFiles?.length}
          >
            {uploadingReportFile ? 'Subiendo...' : `Subir archivo${reportFiles?.length > 1 ? 's' : ''}`}
          </Button>
          {reportFiles?.length > 0 && (
            <div className="space-y-1.5 max-h-28 overflow-y-auto pr-1">
              {reportFiles.map((file, idx) => (
                <div
                  key={`${file.name}-${file.size}-${file.lastModified}-${idx}`}
                  className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-background/60 px-2.5 py-2"
                >
                  <p className="text-[11px] text-muted-foreground truncate">
                    {file.name}
                  </p>
                  <button
                    type="button"
                    className="shrink-0 rounded-full p-1 text-red-600 hover:bg-red-500/10 transition-colors"
                    onClick={() => clearSelectedReportFile(idx)}
                    disabled={uploadingReportFile}
                    aria-label={`Quitar archivo ${file.name}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {reportUploadError && (
            <p className="text-[11px] text-red-600">{reportUploadError}</p>
          )}
          {reportUploadSuccess && (
            <p className="text-[11px] text-emerald-600">{reportUploadSuccess}</p>
          )}
        </div>
      </form>
    </div>
      <Popup
        isOpen={Boolean(chatToDelete)}
        onClose={() => setChatToDelete(null)}
        onConfirm={() => void confirmDelete()}
        title="Eliminar chat"
        message="¿Seguro que quieres eliminar este chat? Esta acción no se puede deshacer."
        type="confirm"
        confirmText="Eliminar"
        cancelText="Cancelar"
      />
    </>
  );
}

/**
 * Dashboard nuevo — plantilla para ir construyendo sin tocar Dashboard.js.
 */
const DashboardNew = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clientInfo, setClientInfo] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [mortgageRows, setMortgageRows] = useState([]);
  const [mortgageLoading, setMortgageLoading] = useState(true);
  const [mortgageError, setMortgageError] = useState(null);
  const [selectedMortgage, setSelectedMortgage] = useState(null);
  const [savedChats, setSavedChats] = useState([]);
  const [chatsLoading, setChatsLoading] = useState(true);
  const [favorites, setFavorites] = useState([]);
  const [showAllFavorites, setShowAllFavorites] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [reportFiles, setReportFiles] = useState([]);
  const [uploadingReportFile, setUploadingReportFile] = useState(false);
  const [reportUploadError, setReportUploadError] = useState('');
  const [reportUploadSuccess, setReportUploadSuccess] = useState('');
  const [uploadedDocumentsCount, setUploadedDocumentsCount] = useState(0);
  const [validatedDocumentKeys, setValidatedDocumentKeys] = useState([]);
  const [affordability, setAffordability] = useState(null);
  const [affordabilityLoading, setAffordabilityLoading] = useState(true);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [editData, setEditData] = useState({});

  const closeMortgageDetail = useCallback(() => setSelectedMortgage(null), []);

  const handleOpenChat = useCallback(
    async (chatId) => {
      if (!user?.uid) return;
      const chat = await loadChat(user.uid, chatId);
      if (chat) {
        navigate('/chat', { state: { loadedChat: chat } });
      }
    },
    [navigate, user?.uid]
  );

  const handleRenameChat = useCallback(
    async (chatId, nextName) => {
      if (!user?.uid) return;
      await renameChat(user.uid, chatId, nextName);
      setSavedChats((prev) =>
        prev.map((chat) =>
          chat.id === chatId ? { ...chat, chat_name: nextName, updated_at: new Date().toISOString() } : chat
        )
      );
    },
    [user?.uid]
  );

  const handleDeleteChat = useCallback(
    async (chatId) => {
      if (!user?.uid) return;
      await deleteChat(user.uid, chatId);
      setSavedChats((prev) => prev.filter((chat) => chat.id !== chatId));
    },
    [user?.uid]
  );

  useEffect(() => {
    let cancelled = false;
    const email = user?.email;
    if (!email) {
      setProfileLoading(false);
      setMortgageLoading(false);
      setChatsLoading(false);
      setFavorites([]);
      setAffordabilityLoading(false);
      setAffordability(null);
      setClientInfo(null);
      setMortgageRows([]);
      setSavedChats([]);
      setUploadedDocumentsCount(0);
      return;
    }
    (async () => {
      setProfileLoading(true);
      setMortgageLoading(true);
      setChatsLoading(true);
      setAffordabilityLoading(true);
      setMortgageError(null);
      const settled = await Promise.allSettled([
        getClientInfo(email),
        getMortgageRecs(email),
        user.uid ? getUserSavedChats(user.uid) : Promise.resolve([]),
        user.uid ? getUserFavorites(user.uid) : Promise.resolve([]),
        getClientsAffordability(email),
      ]);
      if (cancelled) return;
      const [rClient, rMortgage, rChats, rFavorites, rAfford] = settled;
      if (rClient.status === 'fulfilled') {
        setClientInfo(rClient.value);
        setEditData(rClient.value || {});
      } else {
        setClientInfo(null);
        setEditData({});
      }
      if (rMortgage.status === 'fulfilled') {
        setMortgageRows(rMortgage.value || []);
        setMortgageError(null);
      } else {
        setMortgageRows([]);
        setMortgageError(
          rMortgage.reason?.message || 'No se pudieron cargar las hipotecas'
        );
      }
      if (rChats.status === 'fulfilled') {
        setSavedChats(rChats.value || []);
      } else {
        setSavedChats([]);
      }
      if (rFavorites.status === 'fulfilled') {
        setFavorites(rFavorites.value || []);
      } else {
        setFavorites([]);
      }
      if (rAfford.status === 'fulfilled') {
        setAffordability(rAfford.value);
      } else {
        setAffordability(null);
      }
      setProfileLoading(false);
      setMortgageLoading(false);
      setChatsLoading(false);
      setAffordabilityLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.email, user?.uid]);

  const visibleFavorites = showAllFavorites ? favorites : favorites.slice(0, 3);

  const handleStartEditProfile = () => {
    setEditData(clientInfo || {});
    setIsEditingProfile(true);
  };

  const handleCancelEditProfile = () => {
    setEditData(clientInfo || {});
    setIsEditingProfile(false);
  };

  const handleProfileInputChange = (field, value) => {
    setEditData((prev) => ({
      ...prev,
      [field]: value === '' ? null : value,
    }));
  };

  const handleSaveEditProfile = async () => {
    if (!user?.email) return;
    setIsSavingProfile(true);
    try {
      const cleanedData = { ...editData };
      const numericFields = ['age', 'min_price', 'max_price', 'min_size', 'bedrooms', 'bathrooms'];
      numericFields.forEach((field) => {
        if (cleanedData[field] === '' || cleanedData[field] === null || cleanedData[field] === undefined) {
          cleanedData[field] = null;
        } else {
          const numValue =
            typeof cleanedData[field] === 'string' ? parseFloat(cleanedData[field]) : cleanedData[field];
          cleanedData[field] = Number.isNaN(numValue) ? null : numValue;
        }
      });

      // Limitar valores a los mismos catálogos usados en el dashboard antiguo.
      if (cleanedData.age != null) {
        cleanedData.age = Math.max(18, Math.min(100, cleanedData.age));
      }
      if (cleanedData.min_price != null && !minPriceOptions.includes(cleanedData.min_price)) {
        cleanedData.min_price = null;
      }
      if (cleanedData.max_price != null && !maxPriceOptions.includes(cleanedData.max_price)) {
        cleanedData.max_price = null;
      }
      if (cleanedData.min_size != null && !minSizeOptions.includes(cleanedData.min_size)) {
        cleanedData.min_size = null;
      }
      if (cleanedData.bedrooms != null && !bedroomsOptions.includes(cleanedData.bedrooms)) {
        cleanedData.bedrooms = null;
      }
      if (cleanedData.bathrooms != null && !bathroomsOptions.includes(cleanedData.bathrooms)) {
        cleanedData.bathrooms = null;
      }

      await updateClientInfo(user.email, cleanedData);
      setClientInfo(cleanedData);
      setIsEditingProfile(false);
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleReportFileUpload = async (e) => {
    e.preventDefault();
    setReportUploadError('');
    setReportUploadSuccess('');

    if (!reportFiles.length) {
      setReportUploadError('Selecciona al menos un PDF antes de enviarlo.');
      return;
    }
    const isAllowedFile = (file) => {
      const name = String(file.name || '').toLowerCase();
      const allowedMime = ['application/pdf', 'image/jpeg'];
      const allowedExt = ['.pdf', '.jpg', '.jpeg'];
      return allowedMime.includes(file.type) || allowedExt.some((ext) => name.endsWith(ext));
    };
    const invalidTypeFile = reportFiles.find((file) => !isAllowedFile(file));
    if (invalidTypeFile) {
      setReportUploadError(`Solo se permiten archivos PDF/JPG/JPEG. Revisa: ${invalidTypeFile.name}`);
      return;
    }
    const oversizedFile = reportFiles.find((file) => file.size > 20 * 1024 * 1024);
    if (oversizedFile) {
      setReportUploadError(`El archivo ${oversizedFile.name} supera 20MB.`);
      return;
    }

    try {
      setUploadingReportFile(true);
      const webhookUrl = process.env.REACT_APP_N8N_WEBHOOK_FILE_UPLOAD;
      if (!webhookUrl) {
        throw new Error('Falta REACT_APP_N8N_WEBHOOK_FILE_UPLOAD en .env');
      }

      const savedDocs = [];
      const saveFailedFiles = [];
      const fileSignature = (file) => `${file.name}-${file.size}-${file.lastModified}`;

      // 1) Guardar todos los archivos en Supabase (Storage + tabla property_reports)
      for (const file of reportFiles) {
        try {
          const uploadedDoc = await uploadPropertyReportPdf({
            userEmail: user?.email || '',
            file,
          });
          const reportRecord = await createPropertyReportRecord({
            userEmail: user?.email || '',
            fileName: file.name,
            fileUrl: uploadedDoc.url,
            filePath: uploadedDoc.path,
          });
          savedDocs.push({
            file,
            url: uploadedDoc.url,
            path: uploadedDoc.path,
            reportId: reportRecord?.id || null,
          });
        } catch (fileErr) {
          saveFailedFiles.push({
            name: file.name,
            signature: fileSignature(file),
            message: fileErr instanceof Error ? fileErr.message : 'No se pudo guardar este archivo en Supabase.',
          });
        }
      }

      // 2) Validar en webhook uno a uno (solo los guardados correctamente).
      let validatedOk = 0;
      let rejectedNotExpected = 0;
      const validationFailedFiles = [];
      const newValidatedKeys = [];
      const validationLabels = [];
      const removedNotExpectedNames = [];

      for (const doc of savedDocs) {
        try {

          const response = await fetch(webhookUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              documentUrl: doc.url,
              pdfUrl: doc.url,
              pdfPath: doc.path,
              fileName: doc.file.name,
              userEmail: user?.email || '',
              userName: clientInfo?.name || user?.displayName || '',
              source: 'dashboard_report_upload',
              uploadedAt: new Date().toISOString(),
            }),
          });
          if (!response.ok) {
            const errText = await response.text().catch(() => '');
            throw new Error(errText || `Error ${response.status} del servidor`);
          }
          const rawWebhookBody = await response.text().catch(() => '');
          const responseData = parseWebhookPayload(rawWebhookBody);
          const matchedKey = extractValidatedDocumentKeyFromWebhook(responseData);
          const expectedFlag = extractWebhookExpectedFlag(responseData);
          const isExpectedDocument = expectedFlag === null ? Boolean(matchedKey) : Boolean(expectedFlag);

          if (isExpectedDocument && matchedKey) {
            const movedDoc = await movePropertyReportToValidated({
              userEmail: user?.email || '',
              fromPath: doc.path,
              fileName: doc.file.name,
            });
            if (doc.reportId) {
              await updatePropertyReportRecordLocation({
                reportId: doc.reportId,
                filePath: movedDoc.path,
                fileUrl: movedDoc.url,
                fileName: doc.file.name,
              });
            }
            validatedOk += 1;
            newValidatedKeys.push(matchedKey);
            const matchedDoc = REQUIRED_UPLOAD_DOCUMENTS.find((item) => item.key === matchedKey);
            if (matchedDoc) {
              validationLabels.push(matchedDoc.label);
            }
          } else if (isExpectedDocument && !matchedKey) {
            // Evitamos borrar datos si el webhook dice "valido" pero no devuelve tipo claro.
            throw new Error(
              'El webhook marco el archivo como valido, pero no devolvio documentKey reconocible.'
            );
          } else {
            // No coincide con la documentación requerida -> borrar de Supabase.
            await deletePropertyReportAsset(doc.path);
            if (doc.reportId) {
              await deletePropertyReportRecord(doc.reportId);
            }
            rejectedNotExpected += 1;
            removedNotExpectedNames.push(doc.file.name);
          }
        } catch (validationErr) {
          validationFailedFiles.push({
            name: doc.file.name,
            signature: fileSignature(doc.file),
            message:
              validationErr instanceof Error
                ? validationErr.message
                : 'No se pudo validar este archivo en el webhook.',
          });
        }
      }

      if (newValidatedKeys.length > 0) {
        setValidatedDocumentKeys((prev) => {
          const next = Array.from(new Set([...prev, ...newValidatedKeys]));
          setUploadedDocumentsCount(next.length);
          return next;
        });
      }

      const totalFailed = saveFailedFiles.length + validationFailedFiles.length;
      if (totalFailed === 0) {
        const validatedText =
          validationLabels.length > 0
            ? ` Validados: ${Array.from(new Set(validationLabels)).join(', ')}.`
            : '';
        const removedText =
          rejectedNotExpected > 0
            ? ` ${rejectedNotExpected} archivo(s) no coincidian con lo solicitado y se eliminaron de Supabase (${removedNotExpectedNames.join(', ')}).`
            : '';
        setReportUploadSuccess(
          validatedOk === 1
            ? `1 archivo validado y movido a "validados".${validatedText}${removedText}`
            : `${validatedOk} archivos validados y movidos a "validados".${validatedText}${removedText}`
        );
        setReportFiles([]);
      } else if (savedDocs.length === 0) {
        const firstCause = saveFailedFiles[0]?.message ? ` Motivo: ${saveFailedFiles[0].message}` : '';
        const failedNames = saveFailedFiles.map((f) => f.name).join(', ');
        setReportUploadError(
          `No se pudo guardar ningún archivo en Supabase. Revisa: ${failedNames}.${firstCause}`
        );
        const failedSignatures = new Set(saveFailedFiles.map((f) => f.signature));
        setReportFiles((prev) => prev.filter((f) => failedSignatures.has(fileSignature(f))));
      } else {
        const validationFailedNames = validationFailedFiles.map((f) => f.name).join(', ');
        const firstValidationCause = validationFailedFiles[0]?.message
          ? ` Motivo: ${validationFailedFiles[0].message}`
          : '';
        const validatedText =
          validationLabels.length > 0
            ? ` Validados: ${Array.from(new Set(validationLabels)).join(', ')}.`
            : '';
        const removedText =
          rejectedNotExpected > 0
            ? ` ${rejectedNotExpected} archivo(s) no coincidian y se eliminaron de Supabase (${removedNotExpectedNames.join(', ')}).`
            : '';
        setReportUploadSuccess(
          `${validatedOk} archivo(s) validados y movidos a "validados".${validatedText}${removedText}`
        );
        setReportUploadError(
          validationFailedFiles.length > 0
            ? `Guardados, pero ${validationFailedFiles.length} no se pudieron validar en webhook: ${validationFailedNames}.${firstValidationCause}`
            : ''
        );
        const failedSignatures = new Set(saveFailedFiles.map((f) => f.signature));
        setReportFiles((prev) => prev.filter((f) => failedSignatures.has(fileSignature(f))));
      }
    } catch (err) {
      setReportUploadError(err instanceof Error ? err.message : 'No se pudo subir el archivo.');
    } finally {
      setUploadingReportFile(false);
    }
  };

  return (
    <div className="h-dvh max-h-dvh min-h-0 w-full max-w-[100vw] overflow-x-hidden overflow-y-hidden flex flex-col bg-background relative">
      {selectedMortgage && (
        <MortgageDetailModal row={selectedMortgage} onClose={closeMortgageDetail} />
      )}
      <div
        className="pointer-events-none fixed inset-0 -z-10 opacity-20 dark:opacity-25"
        style={{
          background:
            'radial-gradient(ellipse 80% 50% at 50% -20%, hsl(var(--primary) / 0.22), transparent), radial-gradient(ellipse 60% 40% at 100% 50%, hsl(var(--primary) / 0.08), transparent)',
        }}
      />
      <Navbar />
      {showMobileSidebar && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowMobileSidebar(false)}
            aria-label="Cerrar panel de chats"
          />
          <div className="absolute inset-y-0 left-0 w-[90vw] max-w-sm p-3">
            <div className="h-full flex flex-col">
              <div className="flex justify-end mb-2">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="rounded-full bg-background/90"
                  onClick={() => setShowMobileSidebar(false)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex-1 min-h-0">
                <UserChatsSidebar
                  chats={savedChats}
                  loading={chatsLoading}
                  onOpenChat={async (chatId) => {
                    await handleOpenChat(chatId);
                    setShowMobileSidebar(false);
                  }}
                  onRenameChat={handleRenameChat}
                  onDeleteChat={handleDeleteChat}
                  reportFiles={reportFiles}
                  setReportFiles={setReportFiles}
                  reportUploadError={reportUploadError}
                  reportUploadSuccess={reportUploadSuccess}
                  uploadingReportFile={uploadingReportFile}
                  onUploadReportFile={handleReportFileUpload}
                  uploadedDocumentsCount={uploadedDocumentsCount}
                  documentsRequiredTotal={DOCUMENTS_REQUIRED_TOTAL}
                  validatedDocumentKeys={validatedDocumentKeys}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      <main className="flex flex-1 flex-col min-h-0 w-full box-border px-3 sm:px-5 md:px-6 lg:px-8 xl:px-10 2xl:px-14 pt-16 pb-3 sm:pb-4 overflow-y-auto overscroll-contain lg:overflow-hidden lg:h-[calc(100dvh-4rem)] lg:max-h-[calc(100dvh-4rem)]">
        {user?.email && (
          <div className="flex flex-col lg:flex-row gap-5 sm:gap-6 lg:gap-8 xl:gap-10 flex-1 min-h-0 lg:h-full lg:overflow-hidden">
            <aside className="hidden lg:flex w-full shrink-0 flex-col min-h-0 lg:h-full lg:w-[min(100%,18rem)] xl:w-80 2xl:w-96">
              <UserChatsSidebar
                chats={savedChats}
                loading={chatsLoading}
                onOpenChat={handleOpenChat}
                onRenameChat={handleRenameChat}
                onDeleteChat={handleDeleteChat}
                reportFiles={reportFiles}
                setReportFiles={setReportFiles}
                reportUploadError={reportUploadError}
                reportUploadSuccess={reportUploadSuccess}
                uploadingReportFile={uploadingReportFile}
                onUploadReportFile={handleReportFileUpload}
                uploadedDocumentsCount={uploadedDocumentsCount}
                documentsRequiredTotal={DOCUMENTS_REQUIRED_TOTAL}
                validatedDocumentKeys={validatedDocumentKeys}
              />
            </aside>

            <div className="flex-1 min-w-0 min-h-0 w-full space-y-4 sm:space-y-6 lg:space-y-8 lg:overflow-y-auto lg:overscroll-contain lg:pr-1">
              <div className="lg:hidden">
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-full"
                  onClick={() => setShowMobileSidebar(true)}
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Ver chats y subir PDF
                </Button>
              </div>
              <UserFinancialProfile
                client={clientInfo}
                loading={profileLoading}
                affordability={affordability}
                affordabilityLoading={affordabilityLoading}
                isEditing={isEditingProfile}
                isSaving={isSavingProfile}
                editData={editData}
                onStartEdit={handleStartEditProfile}
                onCancelEdit={handleCancelEditProfile}
                onSaveEdit={handleSaveEditProfile}
                onInputChange={handleProfileInputChange}
              />

              <section className="relative glass rounded-3xl border border-primary/15 p-5 sm:p-6 shadow-sm dark:shadow-xl">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2.5 rounded-xl bg-gradient-to-br from-primary/15 to-violet-500/10 ring-1 ring-primary/20">
                      <Heart className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-lg sm:text-xl font-bold tracking-tight">Viviendas favoritas</h2>
                      <p className="text-sm text-muted-foreground mt-0.5">
                        {favorites.length === 0
                          ? 'Guarda viviendas en favoritos y aparecerán aquí.'
                          : `${favorites.length} guardada${favorites.length === 1 ? '' : 's'} en total.`}
                      </p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" className="rounded-full" asChild>
                    <Link to="/favorites">Ver todas</Link>
                  </Button>
                </div>

                {favorites.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-border bg-muted/15 px-5 py-10 text-center">
                    <Heart className="w-10 h-10 mx-auto text-muted-foreground/60 mb-3" />
                    <p className="text-sm text-muted-foreground">
                      Todavía no tienes viviendas favoritas.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="grid gap-3 sm:gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
                      {visibleFavorites.map((favorite) => (
                        <article
                          key={favorite.id}
                          className="rounded-2xl border border-border/60 bg-background/70 overflow-hidden"
                        >
                          {(() => {
                            const rawImageUrl =
                              favorite.property_image ||
                              favorite.thumbnail ||
                              favorite.image ||
                              favorite.images?.[0] ||
                              favorite.photo ||
                              favorite.photos?.[0] ||
                              favorite.mainImage ||
                              favorite.picture ||
                              favorite.media?.[0] ||
                              favorite.mediaUrl ||
                              favorite.imageUrl ||
                              favorite.visual?.[0] ||
                              favorite.visuals?.[0] ||
                              favorite.mediaFiles?.[0] ||
                              favorite.attachments?.[0] ||
                              null;
                            const imageUrl = rawImageUrl
                              ? `https://images.weserv.nl/?url=${encodeURIComponent(rawImageUrl)}`
                              : null;
                            const rooms = favorite.property_rooms ?? favorite.property_details?.rooms;
                            const bathrooms = favorite.property_bathrooms ?? favorite.property_details?.bathrooms;
                            const size = favorite.property_size ?? favorite.property_details?.size;

                            return (
                              <>
                                <div className="relative h-44 bg-secondary/50">
                                  <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
                                    <div className="text-center">
                                      <Square className="w-10 h-10 mx-auto mb-1.5 opacity-70" />
                                      <p className="text-xs">Sin imagen</p>
                                    </div>
                                  </div>
                                  {imageUrl && (
                                    <img
                                      src={imageUrl}
                                      alt={favorite.property_title || 'Propiedad'}
                                      className="absolute inset-0 w-full h-full object-cover"
                                      onError={(e) => {
                                        e.currentTarget.style.display = 'none';
                                      }}
                                    />
                                  )}
                                </div>

                                <div className="p-4">
                                  <p className="text-sm font-semibold text-foreground line-clamp-2">
                                    {favorite.property_title || 'Propiedad sin título'}
                                  </p>
                                  <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                                    {favorite.property_location || 'Ubicación no disponible'}
                                  </p>

                                  <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground">
                                    {rooms ? (
                                      <span className="inline-flex items-center gap-1">
                                        <Bed className="w-3.5 h-3.5" />
                                        {rooms} hab
                                      </span>
                                    ) : null}
                                    {bathrooms ? (
                                      <span className="inline-flex items-center gap-1">
                                        <Bath className="w-3.5 h-3.5" />
                                        {bathrooms} baños
                                      </span>
                                    ) : null}
                                    {size ? (
                                      <span className="inline-flex items-center gap-1">
                                        <Square className="w-3.5 h-3.5" />
                                        {size} m²
                                      </span>
                                    ) : null}
                                  </div>

                                  <div className="mt-3 flex items-center justify-between gap-2">
                                    <p className="text-base font-semibold text-primary">
                                      {favorite.property_price
                                        ? formatEuro(favorite.property_price)
                                        : 'Precio no disponible'}
                                    </p>
                                    {favorite.property_url ? (
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        className="rounded-full h-8 px-3"
                                        onClick={() => window.open(favorite.property_url, '_blank', 'noopener,noreferrer')}
                                      >
                                        <ExternalLink className="w-3.5 h-3.5 mr-1" />
                                        Ver
                                      </Button>
                                    ) : null}
                                  </div>
                                </div>
                              </>
                            );
                          })()}
                        </article>
                      ))}
                    </div>

                    {favorites.length > 3 && (
                      <div className="mt-4 flex justify-center">
                        <Button
                          type="button"
                          variant="outline"
                          className="rounded-full"
                          onClick={() => setShowAllFavorites((prev) => !prev)}
                        >
                          {showAllFavorites ? 'Ver menos' : `Ver más (${favorites.length - 3})`}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </section>

              <section className="relative glass rounded-3xl border border-primary/15 p-5 sm:p-6 shadow-sm dark:shadow-xl">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="p-2.5 rounded-xl bg-gradient-to-br from-primary/15 to-indigo-500/10 ring-1 ring-primary/20">
                      <FileText className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-lg sm:text-xl font-bold tracking-tight">Informes</h2>
                      <p className="text-sm text-muted-foreground mt-0.5">
                        Accede a todos tus documentos e informes generados.
                      </p>
                      <div className="mt-3">
                        <Button variant="hero" size="sm" className="rounded-full" asChild>
                          <Link to="/reports">
                            <FileText className="w-4 h-4 mr-2" />
                            Ver informes
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section className="relative glass rounded-3xl border border-emerald-500/15 p-5 sm:p-6 shadow-sm dark:shadow-xl">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
              <div className="flex items-start gap-3">
                <div className="p-2.5 rounded-xl bg-gradient-to-br from-emerald-500/15 to-teal-500/10 ring-1 ring-emerald-500/20">
                  <Landmark className="w-7 h-7 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold tracking-tight">Hipotecas pensadas para ti</h2>
                  <p className="text-sm text-muted-foreground mt-1 max-w-none xl:text-base/relaxed leading-relaxed">
                    Ordenadas según tu perfil. Cada tarjeta resume cuota, tipo, financiación y esfuerzo mensual.
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Pulsa una tarjeta para ver el detalle completo (condiciones, notas y más).
                  </p>
                </div>
              </div>
            </div>

            {mortgageLoading && (
              <div className="flex flex-col items-center justify-center gap-3 py-16 sm:py-20 rounded-2xl border border-border/60 bg-muted/10">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground text-center px-4">
                  Buscando las opciones que mejor encajan contigo…
                </p>
              </div>
            )}

            {!mortgageLoading && mortgageError && (
              <div className="flex items-start gap-3 rounded-2xl border border-destructive/25 bg-destructive/[0.06] p-5 text-sm">
                <AlertCircle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-destructive">No pudimos cargar las hipotecas</p>
                  <p className="text-muted-foreground mt-1 leading-relaxed">
                    Ha fallado la conexión o el servidor. Prueba a recargar la página en unos minutos.
                  </p>
                  <p className="text-xs text-muted-foreground mt-3 font-mono break-all opacity-80">
                    {mortgageError}
                  </p>
                </div>
              </div>
            )}

            {!mortgageLoading && !mortgageError && mortgageRows.length === 0 && (
              <div className="rounded-2xl border border-dashed border-border bg-muted/15 px-6 py-12 text-center space-y-3">
                <Landmark className="w-10 h-10 mx-auto text-muted-foreground/50" />
                <p className="text-foreground text-sm font-medium">Todavía no hay ofertas que encajen</p>
                <p className="text-muted-foreground text-sm max-w-md mx-auto leading-relaxed">
                  Cuanto más completa esté tu ficha, mejor podremos recomendarte productos. También puedes volver más tarde.
                </p>
                <Button variant="outline" className="rounded-full mt-2" asChild>
                  <Link to="/dashboard">Revisar o completar datos</Link>
                </Button>
              </div>
            )}

            {!mortgageLoading && !mortgageError && mortgageRows.length > 0 && (
              <div className="grid gap-4 sm:gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 2xl:gap-6">
                {mortgageRows.map((row, idx) => (
                  <MortgageRecommendationCard
                    key={`${row.bank_name}-${row.mortgage_name}-${idx}`}
                    row={row}
                    onOpen={setSelectedMortgage}
                  />
                ))}
              </div>
            )}
              </section>
            </div>
          </div>
        )}

        {!user?.email && (
          <div className="rounded-2xl border border-dashed border-primary/20 bg-muted/15 px-6 py-12 text-center space-y-3 max-w-lg mx-auto">
            <p className="text-base font-semibold text-foreground">Inicia sesión para entrar</p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Así verás tu puntuación, tus chats con GABI y las hipotecas recomendadas para ti.
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default DashboardNew;
